/*
  Everything a BGP alarm recorded before ktrac compiled AlertState.Evidence needs in order to
  render. Such an alarm is readable only through its event view model details array and the
  sanitized vantage point names that array carries, and alert manager records are never migrated,
  so this path has to survive for as long as the UI queries far enough back to reach one.

  It is reachable from exactly two call sites, both in synthetics/bgp.js: summarizeBgpContext and
  parseVantagePointName. Deleting the path is deleting this file, those two fall-throughs, and
  app/views/synthetics/utils/bgpLegacy.js with its call sites.
*/

/*
  Maps the 'name' of an entry in an alarm's event view model details array to the property we
  store it under in alarm.details.bgp

  The source of truth for these names is ktrac (pkg/bgptest/evm.go). Entries are conditional:
  - TestType / monitored-prefix are always present
  - vp-count / current-vps / prefix-list / vp-list are present whenever there is alert context
    (they are absent on CLEAR events)
  - the remainder are specific to a single condition type
*/
const BGP_ALARM_CONTEXT_FIELDS = {
  TestType: 'testType',
  'monitored-prefix': 'monitoredPrefix',
  'vp-count': 'vpCount',
  'current-vps': 'totalVps',
  'bad-origins': 'badOrigins',
  'rpki-summary': 'rpkiStatus',
  'bad-upstreams': 'badUpstreams',
  'current-reachability': 'currentReachability',
  'unreachable-vps': 'unreachableVps',
  'prefix-list': 'affectedPrefixes',
  'vp-list': 'affectedVps'
};

// ktrac caps the vantage point lists and appends this sentinel to indicate there were more
const BGP_TRUNCATION_SENTINEL = '...';

// list fields that ktrac may truncate, mapped to the flag we set when it has
const BGP_TRUNCATABLE_FIELDS = {
  affectedPrefixes: 'affectedPrefixesTruncated',
  affectedVps: 'affectedVpsTruncated',
  unreachableVps: 'unreachableVpsTruncated'
};

// ktrac sends a single-entry list as a bare scalar; consumers iterate these unconditionally
const BGP_LIST_FIELDS = new Set(['badOrigins', 'badUpstreams', 'affectedPrefixes', 'affectedVps', 'unreachableVps']);

/*
  ASNs arrive from the alerting backend as 'AS####' strings; the numeric value is what ASNRenderer
  and the incident log's grouping key need.
*/
const parseLegacyAsn = (value) => {
  const match = /^\s*(?:AS)?(\d+)\s*$/i.exec(String(value ?? ''));

  return match ? Number(match[1]) : null;
};

/*
  Given the 'details' array from an alarm's event view model, returns a structured summary of the
  bgp-specific statistics suitable for merging into alarm.details.bgp

  Missing entries are omitted rather than set to null so that consumers can treat any present key
  as displayable. The 'legacy' marker is always set, since it records which path produced the
  object rather than anything the alarm carried, and the drawer's legacy rows key off it.
*/
const summarizeLegacyBgpContext = (details) => {
  const summary = { legacy: true };

  if (!Array.isArray(details)) {
    return summary;
  }

  details.forEach((detail) => {
    const key = BGP_ALARM_CONTEXT_FIELDS[detail?.name];

    if (!key) {
      return;
    }

    const rawValue = detail.value;

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return;
    }

    const value = BGP_LIST_FIELDS.has(key) && !Array.isArray(rawValue) ? [rawValue] : rawValue;

    if (Array.isArray(value)) {
      const truncationFlag = BGP_TRUNCATABLE_FIELDS[key];
      const truncated = Boolean(truncationFlag && value[value.length - 1] === BGP_TRUNCATION_SENTINEL);
      const list = (truncated ? value.slice(0, -1) : value).filter(
        (item) => item !== null && item !== undefined && item !== ''
      );

      if (truncated) {
        summary[truncationFlag] = true;
      }

      if (list.length === 0) {
        return;
      }

      summary[key] = list;
      return;
    }

    summary[key] = value;
  });

  /*
    The event view model carries the VP count only as display text ('104 (26.74%)'), so recover the
    magnitude from it. Consumers ranking alarms read affectedVpCount whichever path produced them.
  */
  const affectedVpCount = /^\s*(\d+)/.exec(String(summary.vpCount ?? ''));

  if (affectedVpCount) {
    summary.affectedVpCount = Number(affectedVpCount[1]);
  }

  /*
    These alarms carry no vantage point weight per ASN, so the first invalid origin, then the first
    invalid upstream, stands in for the ranked top ASN an evidence-bearing alarm supplies. The
    incident log sorts and groups on it either way.
  */
  const topFlaggedAsn = [...(summary.badOrigins || []), ...(summary.badUpstreams || [])]
    .map(parseLegacyAsn)
    .find((asn) => asn !== null);

  if (topFlaggedAsn !== undefined) {
    summary.topFlaggedAsn = topFlaggedAsn;
  }

  return summary;
};

/*
  Parses route.VantagePoint.SanitizedString():
  '<dataset>.<collector>.as-<ASN>.<ipv4|ipv6>-<IP>'.

  The sanitized form replaces every non-alphanumeric character with '-', so IP octets/hextets are
  dash-separated and need converting back. Any trailing afi/safi parts are ignored.
*/
const parseLegacyVantagePointName = (name) => {
  const [dataset, collector, asnPart, peerPart] = name.split('.');

  if (!dataset || !collector || !asnPart || !peerPart) {
    return null;
  }

  const asnMatch = /^as-(\d+)$/i.exec(asnPart);
  const peerMatch = /^(ipv4|ipv6)-(.+)$/i.exec(peerPart);

  if (!asnMatch || !peerMatch) {
    return null;
  }

  const [, family, sanitizedIp] = peerMatch;
  const separator = family.toLowerCase() === 'ipv6' ? ':' : '.';

  return {
    name,
    dataset,
    collector,
    peerAsn: Number(asnMatch[1]),
    asn: `AS${asnMatch[1]}`,
    ip: sanitizedIp.split('-').join(separator)
  };
};

module.exports = {
  parseLegacyAsn,
  summarizeLegacyBgpContext,
  parseLegacyVantagePointName
};
